import type { CustomerQuoteBudgetReturnContext, CustomerQuoteBudgetReturnResult } from "../types/budgetReturn";
/** 对客报价后端 snake_case 与前端 camelCase 的唯一映射层。 */

import type { components } from "../../../api/generated";
import type {
  CustomerQuoteAllocationResult,
  CustomerQuoteEditorData,
  CustomerQuoteLine,
  CustomerQuotePreview,
  CustomerQuoteRoleSummary,
  CustomerQuoteSnapshot,
  CustomerQuoteSourceChange,
  CustomerQuoteSummary,
  CustomerQuoteVersion,
  EnterCustomerQuoteResult,
  SaveCustomerQuoteDraftRequest,
} from "../types";

type Raw = Record<string, unknown>;
type QuoteAllocationRequestContract = components["schemas"]["QuoteAllocationRequest"];
export type QuoteAllocationResultContract = components["schemas"]["QuoteAllocationResult"];
export type BudgetReturnResultContract = components["schemas"]["BudgetReturnResult"];
export type BudgetReturnContextContract = components["schemas"]["BudgetReturnContext"];

/** 仅在前端请求适配层使用的隐藏项合并组，接口仍负责真实金额计算。 */
export const CUSTOMER_QUOTE_HIDDEN_ALLOCATION_GROUP = "__customer_quote_hidden_items__";

const str = (value: unknown, fallback = "") =>
  value === null || value === undefined ? fallback : String(value);
const num = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const optNum = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
const bool = (value: unknown, fallback = false) =>
  value === null || value === undefined ? fallback : Boolean(value);
const optStr = (value: unknown): string | null =>
  value === null || value === undefined ? null : String(value);
const records = (value: unknown): Raw[] => (Array.isArray(value) ? (value as Raw[]) : []);
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((item) => String(item)) : [];

/** 将未知值收窄为 JSON 普通对象，避免脏数组元素在映射时解引用失败。 */
function plainRaw(value: unknown): Raw | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null ? value as Raw : null;
}

/** 校验并按十进制半上规则规范客户明细金额，拒绝 DECIMAL(18,2) 外的数据。 */
function previewDetailAmount(value: unknown): string | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  if (typeof value === "number" && !Number.isFinite(value)) return null;
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(String(value).trim());
  if (!match) return null;

  const integerDigits = (match[2] ?? "0").replace(/^0+(?=\d)/, "");
  const fractionDigits = match[3] ?? "";
  let absoluteCents = BigInt(integerDigits) * 100n
    + BigInt(`${fractionDigits}00`.slice(0, 2));
  if ((fractionDigits[2] ?? "0") >= "5") absoluteCents += 1n;
  if (absoluteCents > 999999999999999999n) return null;

  const integerPart = absoluteCents / 100n;
  const decimalPart = String(absoluteCents % 100n).padStart(2, "0");
  const sign = match[1] === "-" && absoluteCents !== 0n ? "-" : "";
  return `${sign}${integerPart}.${decimalPart}`;
}

/** 映射内部报价行，来源快照与当前计算值不得混用。 */
export function mapCustomerQuoteLine(raw: Raw): CustomerQuoteLine {
  return {
    customerQuoteLineId: optStr(raw.customer_quote_line_id) ?? undefined,
    lineOrigin: str(raw.line_origin, "budget") as CustomerQuoteLine["lineOrigin"],
    sourceBudgetLineId: optStr(raw.source_budget_line_id),
    lineNo: num(raw.line_no),
    resourceType: str(raw.resource_type),
    feeCategory: str(raw.fee_category, "misc") as CustomerQuoteLine["feeCategory"],
    travelerRole: optStr(raw.traveler_role),
    sourceQuantity: optStr(raw.source_quantity),
    sourcePeopleCount: optNum(raw.source_people_count),
    quantity: str(raw.quantity, "0"),
    unit: str(raw.unit),
    costUnitPrice: str(raw.cost_unit_price, "0"),
    sourceCostAmount: optStr(raw.source_cost_amount),
    sourceItemName: optStr(raw.source_item_name),
    sourceItemSpec: optStr(raw.source_item_spec),
    sourceResourceEntryType: optStr(raw.source_resource_entry_type),
    sourceSpuTableName: optStr(raw.source_spu_table_name),
    sourceSpuId: optStr(raw.source_spu_id),
    sourceSpuName: optStr(raw.source_spu_name),
    sourceSkuTableName: optStr(raw.source_sku_table_name),
    sourceSkuId: optStr(raw.source_sku_id),
    sourceSkuName: optStr(raw.source_sku_name),
    sourceMealType: optStr(raw.source_meal_type),
    sourceCalcType: optStr(raw.source_calc_type),
    sourcePeopleMode: optStr(raw.source_people_mode),
    sourceManualPeopleCount: optStr(raw.source_manual_people_count),
    sourceIsLinkedToPeopleCount: raw.source_is_linked_to_people_count == null
      ? null
      : bool(raw.source_is_linked_to_people_count),
    sourceIsCapitaShared: raw.source_is_capita_shared == null
      ? null
      : bool(raw.source_is_capita_shared),
    sourceIsAgentCollection: raw.source_is_agent_collection == null
      ? null
      : bool(raw.source_is_agent_collection),
    sourceDayCount: optNum(raw.source_day_count),
    sourceNightCount: optNum(raw.source_night_count),
    sourcePriceSourceType: optStr(raw.source_price_source_type),
    sourcePriceSourceRef: optStr(raw.source_price_source_ref),
    sourcePriceBasisDesc: optStr(raw.source_price_basis_desc),
    sourceEntrySnapshot: plainRaw(raw.source_entry_snapshot),
    costAmount: str(raw.cost_amount, "0"),
    quoteUnitPrice: str(raw.quote_unit_price, "0"),
    quoteAmount: str(raw.quote_amount, "0"),
    customerName: str(raw.customer_name),
    customerDesc: str(raw.customer_desc),
    profitPolicy: str(raw.profit_policy, "none") as CustomerQuoteLine["profitPolicy"],
    displayMode: str(raw.display_mode, "show") as CustomerQuoteLine["displayMode"],
    mergeTargetGroup: optStr(raw.merge_target_group),
    allocationMode: str(raw.allocation_mode, "fixed") as CustomerQuoteLine["allocationMode"],
    isLinkedToPeopleCount: bool(raw.is_linked_to_people_count),
    peopleMode: str(raw.people_mode, "manual"),
    teacherPaymentPolicy: str(
      raw.teacher_payment_policy,
      "not_applicable",
    ) as CustomerQuoteLine["teacherPaymentPolicy"],
    financeTreatment: str(
      raw.finance_treatment,
      "contract_receivable",
    ) as CustomerQuoteLine["financeTreatment"],
    agentCollectionCategory: optStr(raw.agent_collection_category),
    agentCollectionSuggested: bool(raw.agent_collection_suggested),
    isCostLocked: true,
    adjustmentKind: optStr(raw.adjustment_kind) as CustomerQuoteLine["adjustmentKind"],
    adjustmentReason: str(raw.adjustment_reason),
    remark: str(raw.remark),
  };
}

/** 映射完整报价快照。 */
export function mapCustomerQuoteSnapshot(raw: Raw): CustomerQuoteSnapshot {
  return {
    sourceBudgetOrderId: str(raw.source_budget_order_id),
    sourceBudgetVersionNo: num(raw.source_budget_version_no),
    sourceBudgetVersionName: str(raw.source_budget_version_name),
    sourceBudgetUpdatedAt: str(raw.source_budget_updated_at),
    sourceProjectionVersion: optNum(raw.source_projection_version),
    sourceServiceStartDate: optStr(raw.source_service_start_date),
    sourceServiceEndDate: optStr(raw.source_service_end_date),
    sourceStudentCount: num(raw.source_student_count),
    sourceTeacherCount: num(raw.source_teacher_count),
    sourceGuideCount: num(raw.source_guide_count),
    sourceDriverCount: num(raw.source_driver_count),
    studentCount: num(raw.student_count),
    teacherCount: num(raw.teacher_count),
    guideCount: num(raw.guide_count),
    driverCount: num(raw.driver_count),
    sourceTeacherPays: bool(raw.source_teacher_pays),
    sourceTargetMarginRate: optStr(raw.source_target_margin_rate),
    targetMarginRate: str(raw.target_margin_rate, "0"),
    pricingRulesVersion: num(raw.pricing_rules_version, 1),
    stayHighMarkupConfirmed: bool(raw.stay_high_markup_confirmed),
    templateType: str(raw.template_type, "standard") as CustomerQuoteSnapshot["templateType"],
    includedItems: strings(raw.included_items),
    excludedItems: strings(raw.excluded_items),
    lines: records(raw.lines).map(mapCustomerQuoteLine),
  };
}

/** 映射内部可信金额汇总。 */
export function mapCustomerQuoteSummary(raw: Raw): CustomerQuoteSummary {
  return {
    customerPaymentTotalAmount: str(raw.customer_payment_total_amount, "0"),
    agentCollectionTotalAmount: str(raw.agent_collection_total_amount, "0"),
    contractReceivableAmount: str(raw.contract_receivable_amount, "0"),
    internalCostTotalAmount: str(raw.internal_cost_total_amount, "0"),
    expectedGrossProfitAmount: str(raw.expected_gross_profit_amount, "0"),
    expectedMarginRate: str(raw.expected_margin_rate, "0"),
  };
}

/** 映射角色报价汇总。 */
export function mapCustomerQuoteRoleSummary(raw: Raw): CustomerQuoteRoleSummary {
  return {
    studentUnitPrice: str(raw.student_unit_price, "0"),
    teacherUnitPrice: str(raw.teacher_unit_price, "0"),
    fixedContractAmount: str(raw.fixed_contract_amount, "0"),
  };
}

/** 映射报价版本。 */
export function mapCustomerQuoteVersion(raw: Raw): CustomerQuoteVersion {
  return {
    customerQuoteId: str(raw.customer_quote_id),
    budgetOrderId: str(raw.budget_order_id),
    versionNo: num(raw.version_no),
    versionName: str(raw.version_name),
    status: str(raw.status),
    templateType: str(raw.template_type, "standard") as CustomerQuoteVersion["templateType"],
    customerPaymentTotalAmount: str(raw.customer_payment_total_amount, "0"),
    contractReceivableAmount: str(raw.contract_receivable_amount, "0"),
    sourceBudgetVersionNo: num(raw.source_budget_version_no),
    sourceBudgetVersionName: str(raw.source_budget_version_name),
    updatedAt: str(raw.updated_at),
  };
}

/** 映射一项来源核价差异。 */
export function mapCustomerQuoteSourceChange(raw: Raw): CustomerQuoteSourceChange {
  return {
    changeId: str(raw.change_id),
    changeType: str(raw.change_type, "modified") as CustomerQuoteSourceChange["changeType"],
    sourceBudgetLineId: optStr(raw.source_budget_line_id),
    headerField: optStr(raw.header_field),
    feeCategory: optStr(raw.fee_category) as CustomerQuoteSourceChange["feeCategory"],
    itemName: str(raw.item_name),
    resourceType: optStr(raw.resource_type),
    oldCostAmount: optStr(raw.old_cost_amount),
    newCostAmount: optStr(raw.new_cost_amount),
    quoteAmount: optStr(raw.quote_amount),
    oldValue: optStr(raw.old_value),
    newValue: optStr(raw.new_value),
    beforeLine: raw.before_line ? mapCustomerQuoteLine(raw.before_line as Raw) : null,
    afterLine: raw.after_line ? mapCustomerQuoteLine(raw.after_line as Raw) : null,
    previousBaseMarkup: optStr(raw.previous_base_markup),
    suggestedBaseMarkup: optStr(raw.suggested_base_markup),
    requiresMarkupReset: bool(raw.requires_markup_reset),
    linkedLines: records(raw.linked_lines).map((line) => ({
      sourceBudgetLineId: optStr(line.source_budget_line_id),
      lineNo: num(line.line_no),
      itemName: str(line.item_name),
      markupFactor: str(line.markup_factor, "1"),
      previousBaseMarkup: optStr(line.previous_base_markup),
    })),
    changedFields: strings(raw.changed_fields) as CustomerQuoteSourceChange["changedFields"],
  };
}

const sourceBlockReasonMessages: Record<string, string> = {
  roster_pricing_locked: "人员名单已定稿，核价与报价暂不可修改或取消定稿；取消人员名单定稿后解除此限制。",
  source_changed: "来源核价已变化，请在待确认区域逐项核对并设置加价。",
  source_editing: "来源核价正在修改，请等待核价重新定稿后处理差异",
  source_unavailable: "来源核价当前不可用，请稍后刷新后重试。",
  quote_finalized: "报价已定稿，不能修改；如需新的方案，请复制核价版本后重新报价。",
};

/** 将后端阻断原因枚举转换为稳定的简体中文提示。 */
function mapSourceBlockReason(value: unknown): string | null {
  const reason = optStr(value);
  if (reason === null) return null;
  return sourceBlockReasonMessages[reason] ?? "当前报价暂不可操作，请刷新后重试。";
}

/** 映射报价编辑器完整响应。 */
export function mapCustomerQuoteEditor(raw: Raw): CustomerQuoteEditorData {
  const readonly = bool(raw.readonly);
  const sourceBudgetChanged = bool(raw.source_budget_changed);
  return {
    customerQuoteId: str(raw.customer_quote_id),
    itineraryId: str(raw.itinerary_id),
    projectId: optStr(raw.project_id),
    itineraryTitle: typeof raw.itinerary_title === "string" ? raw.itinerary_title.trim() : "",
    budgetOrderId: str(raw.budget_order_id),
    versionNo: num(raw.version_no),
    versionName: str(raw.version_name),
    basedOnCustomerQuoteId: optStr(raw.based_on_customer_quote_id),
    status: str(raw.status),
    remark: str(raw.remark),
    snapshot: mapCustomerQuoteSnapshot((raw.snapshot as Raw) ?? {}),
    summary: mapCustomerQuoteSummary((raw.summary as Raw) ?? {}),
    roleSummary: mapCustomerQuoteRoleSummary((raw.role_summary as Raw) ?? {}),
    versions: records(raw.versions).map(mapCustomerQuoteVersion),
    quoteRevision: str(raw.quote_revision),
    sourceRevision: optStr(raw.source_revision),
    sourceState: str(raw.source_state, "finalized") as CustomerQuoteEditorData["sourceState"],
    sourceChanges: records(raw.source_changes).map(mapCustomerQuoteSourceChange),
    canEditQuote: raw.can_edit_quote == null ? !readonly && !sourceBudgetChanged : bool(raw.can_edit_quote),
    canConfirmSourceChange: bool(raw.can_confirm_source_change),
    canGenerateAutoTarget: raw.can_generate_auto_target == null
      ? !readonly && !sourceBudgetChanged
      : bool(raw.can_generate_auto_target),
    canSave: raw.can_save == null ? !readonly && !sourceBudgetChanged : bool(raw.can_save),
    canCopy: bool(raw.can_copy),
    canFinalize: raw.can_finalize == null ? !readonly && !sourceBudgetChanged : bool(raw.can_finalize),
    sourceBlockReason: mapSourceBlockReason(raw.source_block_reason),
    isRosterPricingLocked: raw.source_block_reason === "roster_pricing_locked",
    sourceBudgetChanged,
    readonly,
  };
}

/** 映射进入唯一报价的结果。 */
export function mapEnterCustomerQuoteResult(raw: Raw): EnterCustomerQuoteResult {
  return {
    customerQuoteId: str(raw.customer_quote_id),
    created: bool(raw.created),
    versionNo: num(raw.version_no),
    redirectTo: str(raw.redirect_to),
  };
}

/** 将完整报价行机械转换为后端字段，不推导来源值。 */
function mapLinePayload(line: CustomerQuoteLine): Raw {
  return {
    customer_quote_line_id: line.customerQuoteLineId ?? null,
    line_origin: line.lineOrigin,
    source_budget_line_id: line.sourceBudgetLineId,
    line_no: line.lineNo,
    resource_type: line.resourceType,
    fee_category: line.feeCategory,
    traveler_role: line.travelerRole,
    source_quantity: line.sourceQuantity,
    ...(line.sourcePeopleCount === undefined ? {} : { source_people_count: line.sourcePeopleCount }),
    quantity: line.quantity,
    unit: line.unit,
    cost_unit_price: line.costUnitPrice,
    source_cost_amount: line.sourceCostAmount,
    source_item_name: line.sourceItemName ?? null,
    source_item_spec: line.sourceItemSpec ?? null,
    source_resource_entry_type: line.sourceResourceEntryType ?? null,
    source_spu_table_name: line.sourceSpuTableName ?? null,
    source_spu_id: line.sourceSpuId ?? null,
    source_spu_name: line.sourceSpuName ?? null,
    source_entry_snapshot: line.sourceEntrySnapshot ?? null,
    source_sku_table_name: line.sourceSkuTableName ?? null,
    source_sku_id: line.sourceSkuId ?? null,
    source_sku_name: line.sourceSkuName ?? null,
    source_meal_type: line.sourceMealType ?? null,
    source_calc_type: line.sourceCalcType ?? null,
    source_people_mode: line.sourcePeopleMode ?? null,
    source_manual_people_count: line.sourceManualPeopleCount ?? null,
    source_is_linked_to_people_count: line.sourceIsLinkedToPeopleCount ?? null,
    source_is_capita_shared: line.sourceIsCapitaShared ?? null,
    source_is_agent_collection: line.sourceIsAgentCollection ?? null,
    source_day_count: line.sourceDayCount ?? null,
    source_night_count: line.sourceNightCount ?? null,
    source_price_source_type: line.sourcePriceSourceType ?? null,
    source_price_source_ref: line.sourcePriceSourceRef ?? null,
    source_price_basis_desc: line.sourcePriceBasisDesc ?? null,
    cost_amount: line.costAmount,
    quote_unit_price: line.quoteUnitPrice,
    quote_amount: line.quoteAmount,
    customer_name: line.customerName,
    customer_desc: line.customerDesc,
    profit_policy: line.profitPolicy,
    display_mode: line.displayMode,
    merge_target_group: line.mergeTargetGroup,
    allocation_mode: line.allocationMode,
    is_linked_to_people_count: line.isLinkedToPeopleCount,
    people_mode: line.peopleMode,
    teacher_payment_policy: line.teacherPaymentPolicy,
    finance_treatment: line.financeTreatment,
    agent_collection_category: line.agentCollectionCategory,
    agent_collection_suggested: line.agentCollectionSuggested,
    is_cost_locked: true,
    adjustment_kind: line.adjustmentKind,
    adjustment_reason: line.adjustmentReason,
    remark: line.remark,
  };
}

/** 将完整快照转换为后端请求体，来源字段只做机械映射。 */
export function mapCustomerQuoteSnapshotPayload(snapshot: CustomerQuoteSnapshot): Raw {
  return {
    source_budget_order_id: snapshot.sourceBudgetOrderId,
    source_budget_version_no: snapshot.sourceBudgetVersionNo,
    source_budget_version_name: snapshot.sourceBudgetVersionName,
    source_budget_updated_at: snapshot.sourceBudgetUpdatedAt,
    source_projection_version: snapshot.sourceProjectionVersion ?? null,
    source_service_start_date: snapshot.sourceServiceStartDate ?? null,
    source_service_end_date: snapshot.sourceServiceEndDate ?? null,
    source_student_count: snapshot.sourceStudentCount,
    source_teacher_count: snapshot.sourceTeacherCount,
    source_guide_count: snapshot.sourceGuideCount,
    source_driver_count: snapshot.sourceDriverCount,
    student_count: snapshot.studentCount,
    teacher_count: snapshot.teacherCount,
    guide_count: snapshot.guideCount,
    driver_count: snapshot.driverCount,
    source_teacher_pays: snapshot.sourceTeacherPays,
    source_target_margin_rate: snapshot.sourceTargetMarginRate,
    target_margin_rate: snapshot.targetMarginRate,
    pricing_rules_version: snapshot.pricingRulesVersion ?? 1,
    stay_high_markup_confirmed: snapshot.stayHighMarkupConfirmed ?? false,
    template_type: snapshot.templateType,
    included_items: snapshot.includedItems,
    excluded_items: snapshot.excludedItems,
    lines: snapshot.lines.map(mapLinePayload),
  };
}

/** 将当前快照精确收窄为分摊请求，并可附带已保存报价版本上下文。 */
export function mapCustomerQuoteAllocationRequest(
  snapshot: CustomerQuoteSnapshot,
  persistence?: { customerQuoteId: string; expectedQuoteRevision: string },
): QuoteAllocationRequestContract {
  return {
    student_count: snapshot.studentCount,
    teacher_count: snapshot.teacherCount,
    source_teacher_pays: snapshot.sourceTeacherPays,
    lines: snapshot.lines.map((line) => ({
      customer_quote_line_id: line.customerQuoteLineId ?? null,
      source_spu_id: line.sourceSpuId ?? null,
      source_spu_name: line.sourceSpuName ?? null,
      source_entry_snapshot: line.sourceEntrySnapshot ?? null,
      quantity: line.quantity,
      unit: line.unit,
      quote_unit_price: line.quoteUnitPrice,
      source_night_count: line.sourceNightCount ?? null,
      source_day_count: line.sourceDayCount ?? null,
      traveler_role: line.travelerRole,
      agent_collection_category: line.agentCollectionCategory,
      fee_category: line.feeCategory,
      customer_name: line.customerName,
      display_mode: line.displayMode === "hide" ? "merge" : line.displayMode,
      merge_target_group: line.displayMode === "hide"
        ? CUSTOMER_QUOTE_HIDDEN_ALLOCATION_GROUP
        : line.mergeTargetGroup,
      allocation_mode: line.allocationMode,
      teacher_payment_policy: line.teacherPaymentPolicy,
      finance_treatment: line.financeTreatment,
      quote_amount: line.quoteAmount,
    })),
    include_agent_collection: true,
    ...(persistence ? {
      customer_quote_id: persistence.customerQuoteId,
      expected_quote_revision: persistence.expectedQuoteRevision,
    } : {}),
  };
}

/** 将人均分摊接口结果映射为稳定的 camelCase 领域结果。 */
export function mapCustomerQuoteAllocationResult(
  raw: QuoteAllocationResultContract,
): CustomerQuoteAllocationResult {
  return {
    studentUnitPrice: raw.student_unit_price,
    teacherUnitPrice: raw.teacher_unit_price,
    upliftTotalAmount: raw.uplift_total_amount,
    unallocatedAmount: raw.unallocated_amount,
    groupBreakdown: raw.breakdown.map((item) => ({
      roundedAmount: item.rounded_amount,
      studentUnitPrice: item.student_unit_price,
      teacherUnitPrice: item.teacher_unit_price,
      sourceLineIds: (item.source_lines ?? []).map((line) => line.line_id ?? null),
    })),
    breakdown: raw.breakdown
      .filter((item) => item.customer_name !== CUSTOMER_QUOTE_HIDDEN_ALLOCATION_GROUP)
      .map((item) => ({
      feeCategory: item.fee_category,
      customerName: item.customer_name,
      quoteAmount: item.quote_amount,
      roundedAmount: item.rounded_amount,
      studentUnitPrice: item.student_unit_price,
      teacherUnitPrice: item.teacher_unit_price,
      sourceLines: (item.source_lines ?? []).map((line) => ({
        lineId: line.line_id ?? null,
        quantity: line.quantity ?? null,
        unit: line.unit ?? null,
        unitPrice: line.unit_price ?? null,
        amount: line.amount ?? null,
        nightCount: line.night_count ?? null,
        dayCount: line.day_count ?? null,
        travelerRole: line.traveler_role ?? null,
        transportCategory: line.transport_category ?? null,
      })),
      })),
    agentCollection: raw.agent_collection ? {
      contractReceivableAmount: raw.agent_collection.contract_receivable_amount,
      agentCollectionTotalAmount: raw.agent_collection.agent_collection_total_amount,
      customerPaymentTotalAmount: raw.agent_collection.customer_payment_total_amount,
      buckets: raw.agent_collection.buckets.map((bucket) => ({
        category: bucket.category,
        amount: bucket.amount,
        sourceLines: bucket.source_lines.map((line) => ({
          lineId: line.line_id,
          amount: line.amount,
          transportCategory: line.transport_category,
        })),
      })),
    } : null,
  };
}

/** 映射保存报价草稿请求。 */
export function mapSaveCustomerQuoteDraftPayload(request: SaveCustomerQuoteDraftRequest): Raw {
  return {
    version_name: request.versionName ?? null,
    remark: request.remark ?? "",
    snapshot: mapCustomerQuoteSnapshotPayload(request.snapshot),
    expected_quote_revision: request.expectedQuoteRevision,
    expected_source_revision: request.expectedSourceRevision,
  };
}

/** 映射核价来源写命令。 */
export function mapCustomerQuoteSourceChangeCommandPayload(request: {
  expectedQuoteRevision: string;
  expectedSourceRevision: string;
}): Raw {
  return {
    expected_quote_revision: request.expectedQuoteRevision,
    expected_source_revision: request.expectedSourceRevision,
  };
}

/** 映射客户安全预览，返回模型没有内部成本和毛利键。 */
export function mapCustomerQuotePreview(raw: Raw): CustomerQuotePreview {
  return {
    itineraryTitle: typeof raw.itinerary_title === "string" ? raw.itinerary_title.trim() : "",
    templateType: str(raw.template_type, "standard") as CustomerQuotePreview["templateType"],
    studentCount: num(raw.student_count),
    teacherCount: num(raw.teacher_count),
    teacherPays: raw.teacher_pays === true,
    teacherPaymentNote: typeof raw.teacher_payment_note === "string"
      ? raw.teacher_payment_note
      : "",
    studentUnitPrice: str(raw.student_unit_price, "0"),
    teacherUnitPrice: str(raw.teacher_unit_price, "0"),
    fixedContractAmount: str(raw.fixed_contract_amount, "0"),
    lines: records(raw.lines).map((line) => ({
      groupName: str(line.group_name),
      description: str(line.description),
      amount: str(line.amount, "0"),
      isAgentCollection: bool(line.is_agent_collection),
      details: records(line.details).flatMap((detailValue) => {
        const detail = plainRaw(detailValue);
        if (!detail) return [];
        const itemName = str(detail.item_name).trim();
        if (!itemName) return [];
        const amount = previewDetailAmount(detail.amount);
        if (amount === null) return [];
        return [{
          itemName,
          description: str(detail.description),
          amount,
        }];
      }),
    })),
    customerPaymentTotalAmount: str(raw.customer_payment_total_amount, "0"),
    agentCollectionTotalAmount: str(raw.agent_collection_total_amount, "0"),
    includedItems: strings(raw.included_items),
    excludedItems: strings(raw.excluded_items),
  };
}

/** 映射一次核价退回的业务与渠道结果。 */
export function mapBudgetReturnResult(raw: BudgetReturnResultContract): CustomerQuoteBudgetReturnResult {
  return {
    returnId: raw.return_id, customerQuoteId: raw.customer_quote_id, budgetOrderId: raw.budget_order_id,
    budgetStatus: raw.budget_status, reason: raw.reason, recipientUserId: raw.recipient_user_id,
    recipientName: raw.recipient_name ?? null, createdAt: raw.created_at, canRetry: raw.can_retry,
    inApp: { status: raw.in_app.status, error: raw.in_app.error ?? null },
    wechat: { status: raw.wechat.status, error: raw.wechat.error ?? null },
  };
}

/** 映射精确核价的退回预检上下文。 */
export function mapBudgetReturnContext(raw: BudgetReturnContextContract): CustomerQuoteBudgetReturnContext {
  return {
    customerQuoteId: raw.customer_quote_id, budgetOrderId: raw.budget_order_id,
    budgetVersionNo: raw.budget_version_no, budgetVersionName: raw.budget_version_name,
    budgetStatus: raw.budget_status, quoteRevision: raw.quote_revision, sourceRevision: raw.source_revision,
    recipientUserId: raw.recipient_user_id, recipientName: raw.recipient_name,
    canReturn: raw.can_return, blockedReason: raw.blocked_reason,
    latestReturn: raw.latest_return ? mapBudgetReturnResult(raw.latest_return) : null,
  };
}
